import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { speakNatural } from "../lib/speak";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * ProactiveAlerts — managers + admin only.
 * Polls /api/alerts/active every 60 s, dedupes by alert id (sessionStorage,
 * scoped to the current tab — not localStorage so it doesn't persist or expose
 * across sessions), speaks new alerts via SpeechSynthesis, and shows a red toast.
 * Honoured the user's mute preference (sessionStorage 'grindrod_alerts_muted').
 */
const SEEN_KEY = "grindrod_alerts_seen_v1";
const MUTED_KEY = "grindrod_alerts_muted";
const POLL_MS = 60_000;

function loadSeen() {
    try {
        return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]"));
    } catch {
        return new Set();
    }
}
function saveSeen(set) {
    try {
        const arr = Array.from(set);
        // Keep only most recent 200 ids
        sessionStorage.setItem(SEEN_KEY, JSON.stringify(arr.slice(-200)));
    } catch { /* ignore */ }
}

export default function ProactiveAlerts() {
    const { user } = useAuth();
    const seenRef = useRef(loadSeen());
    const firstRunRef = useRef(true);
    const [, force] = useState(0);

    const isEligible = user && (user.role === "admin" || user.role === "manager");

    useEffect(() => {
        if (!isEligible) return undefined;
        let cancelled = false;

        const speak = (text) => {
            const muted = sessionStorage.getItem(MUTED_KEY) === "1";
            if (muted) return;
            speakNatural(text);
        };

        const tick = async () => {
            try {
                const { data } = await api.get("/alerts/active");
                const list = data.alerts || [];
                const newOnes = list.filter((a) => !seenRef.current.has(a.id));
                // First run: mark everything seen but DO NOT speak (avoid spam on login)
                if (firstRunRef.current) {
                    list.forEach((a) => seenRef.current.add(a.id));
                    saveSeen(seenRef.current);
                    firstRunRef.current = false;
                    return;
                }
                if (cancelled) return;
                newOnes.forEach((a) => {
                    seenRef.current.add(a.id);
                    const isCritical = a.severity === "critical";
                    if (isCritical) {
                        toast.error(a.message, { duration: 8000, id: a.id });
                    } else if (a.severity === "warning") {
                        toast.warning(a.message, { duration: 7000, id: a.id });
                    } else {
                        toast(a.message, { duration: 6000, id: a.id });
                    }
                    speak(a.message);
                });
                if (newOnes.length > 0) {
                    saveSeen(seenRef.current);
                    force((n) => n + 1);
                }
            } catch { /* ignore */ }
        };

        tick();
        const t = setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [isEligible]);

    return null;
}
